import type { ReactNode } from 'react'
import type {
  LumpsumAllocationCategory,
  LumpsumAllocationInvalidatedRecommendation
} from '../../../../api/allocation.types'
import { LumpsumRecommendationActionType, LumpsumRecommendationSubType } from '../../../../api/common.types'
import {
  PlanCategoryBucketKey,
  PLAN_CATEGORY_BUCKET_TITLES,
  PlansPlanMode,
  type PlansActionFilter
} from '../../../../api/types'
import { usePlanCategoryBucket } from '../../../usePlanCategoryBucketExpansion'
import { BucketAccordion } from '../../../lumpsum-ui-components/BucketAccordion'
import { AllocationInvalidatedFundCard } from '../cards/AllocationInvalidatedFundCard'
import { getCategoryBucketItems } from '../../../../../utils/categoryBucketsUtils'
import { EventNames } from '../../../../../../../../types/misc.types'
import { trackEvent } from '../../../../../../../../utils/appBridge'
import { PlansLumpsumListEventIds, PlansScreen } from '../../../../../analytics/plansEvents'

type AllocationInvalidatedBucketAccordionProps = {
  category: LumpsumAllocationCategory
  categoryId: string
  actionFilter: PlansActionFilter
  isBorderTop?: boolean
}

export function AllocationInvalidatedBucketAccordion({
  category,
  categoryId,
  actionFilter,
  isBorderTop = false
}: AllocationInvalidatedBucketAccordionProps) {
  const invalidatedRecommendations = getCategoryBucketItems<LumpsumAllocationInvalidatedRecommendation>(category?.invalidatedRecommendations)
  const underReviewRecommendations = invalidatedRecommendations.filter(
    (recommendation) =>
      recommendation?.recommendationSubType === LumpsumRecommendationSubType.INVALIDATED_UNDER_REVIEW
  )
  const buyRecommendations = invalidatedRecommendations.filter(
    (recommendation) =>
      recommendation?.recommendationSubType === LumpsumRecommendationSubType.INVALIDATED_ACTIONABLE &&
      recommendation?.actionType === LumpsumRecommendationActionType.BUY
  )
  const sellRecommendations = invalidatedRecommendations.filter(
    (recommendation) =>
      recommendation?.recommendationSubType === LumpsumRecommendationSubType.INVALIDATED_ACTIONABLE &&
      recommendation?.actionType === LumpsumRecommendationActionType.SELL
  )
  const miscRecommendations = invalidatedRecommendations.filter((recommendation) => {
    if (recommendation?.recommendationSubType === LumpsumRecommendationSubType.INVALIDATED_UNDER_REVIEW) {
      return false
    }
    if (
      recommendation?.recommendationSubType === LumpsumRecommendationSubType.INVALIDATED_ACTIONABLE &&
      (recommendation?.actionType === LumpsumRecommendationActionType.BUY ||
        recommendation?.actionType === LumpsumRecommendationActionType.SELL)
    ) {
      return false
    }
    return true
  })
  const { bucketCompositeKey, isExpanded, toggle } = usePlanCategoryBucket({
    planMode: PlansPlanMode.ALLOCATION,
    actionFilter,
    categoryId,
    bucketKey: PlanCategoryBucketKey.invalidatedRecommendations
  })

  return (
    <BucketAccordion
      bucketCompositeKey={bucketCompositeKey}
      title={PLAN_CATEGORY_BUCKET_TITLES[PlanCategoryBucketKey.invalidatedRecommendations]}
      count={invalidatedRecommendations.length}
      isExpanded={isExpanded}
      onToggle={() => {
        trackEvent({
          eventName: EventNames.UserClicked,
          payload: {
            ScreenName: PlansScreen.LumpsumAllocation,
            eventId: PlansLumpsumListEventIds.BucketAccordion,
            buttonLabel: PLAN_CATEGORY_BUCKET_TITLES[PlanCategoryBucketKey.invalidatedRecommendations]
          }
        })
        toggle()
      }}
      isBorderTop={isBorderTop}
      bodyContentClassName="flex flex-col pb-5 pl-1"
    >
      <UnderReviewSubSection
        recommendations={underReviewRecommendations}
        categoryName={category?.categoryName ?? ''}
      />
      <BuySubSection
        recommendations={buyRecommendations}
        categoryName={category?.categoryName ?? ''}
      />
      <SellSubSection
        recommendations={sellRecommendations}
        categoryName={category?.categoryName ?? ''}
      />
      <MiscSubSection
        recommendations={miscRecommendations}
        categoryName={category?.categoryName ?? ''}
      />
    </BucketAccordion>
  )
}

type AllocationInvalidatedSubSectionProps = {
  recommendations: LumpsumAllocationInvalidatedRecommendation[]
  categoryName: string
}

export function UnderReviewSubSection({
  recommendations,
  categoryName
}: AllocationInvalidatedSubSectionProps) {
  if (recommendations.length <= 0) {
    return null
  }

  return (
    <InvalidatedSubSectionLayout title="No longer under Sell later">
      {recommendations.map((recommendation, recommendationIndex) => (
        <div key={`${recommendation?.isin ?? 'fund'}-underReview-${recommendationIndex}`} className={recommendationIndex > 0 ? 'pt-4' : undefined}>
          <AllocationInvalidatedFundCard
            recommendation={recommendation}
            subsectionKey="underReview"
            categoryName={categoryName}
            showBottomDivider={recommendationIndex < recommendations.length - 1}
          />
        </div>
      ))}
    </InvalidatedSubSectionLayout>
  )
}

export function BuySubSection({
  recommendations,
  categoryName
}: AllocationInvalidatedSubSectionProps) {
  if (recommendations.length <= 0) {
    return null
  }

  return (
    <InvalidatedSubSectionLayout title="No longer under Buy">
      {recommendations.map((recommendation, recommendationIndex) => (
        <div key={`${recommendation?.isin ?? 'fund'}-buy-${recommendationIndex}`} className={recommendationIndex > 0 ? 'pt-4' : undefined}>
          <AllocationInvalidatedFundCard
            recommendation={recommendation}
            subsectionKey="buy"
            categoryName={categoryName}
            showBottomDivider={recommendationIndex < recommendations.length - 1}
          />
        </div>
      ))}
    </InvalidatedSubSectionLayout>
  )
}

export function SellSubSection({
  recommendations,
  categoryName
}: AllocationInvalidatedSubSectionProps) {
  if (recommendations.length <= 0) {
    return null
  }

  return (
    <InvalidatedSubSectionLayout title="No longer under Sell">
      {recommendations.map((recommendation, recommendationIndex) => (
        <div key={`${recommendation?.isin ?? 'fund'}-sell-${recommendationIndex}`} className={recommendationIndex > 0 ? 'pt-4' : undefined}>
          <AllocationInvalidatedFundCard
            recommendation={recommendation}
            subsectionKey="sell"
            categoryName={categoryName}
            showBottomDivider={recommendationIndex < recommendations.length - 1}
          />
        </div>
      ))}
    </InvalidatedSubSectionLayout>
  )
}

export function MiscSubSection({
  recommendations,
  categoryName
}: AllocationInvalidatedSubSectionProps) {
  if (recommendations.length <= 0) {
    return null
  }

  return (
    <InvalidatedSubSectionLayout title="Misc.">
      {recommendations.map((recommendation, recommendationIndex) => (
        <div key={`${recommendation?.isin ?? 'fund'}-misc-${recommendationIndex}`} className={recommendationIndex > 0 ? 'pt-4' : undefined}>
          <AllocationInvalidatedFundCard
            recommendation={recommendation}
            subsectionKey="misc"
            categoryName={categoryName}
            showBottomDivider={recommendationIndex < recommendations.length - 1}
          />
        </div>
      ))}
    </InvalidatedSubSectionLayout>
  )
}

function InvalidatedSubSectionLayout({ title, children }: { title: string; children: ReactNode }) {
  return (
    <div
      data-invalidated-subsection
      className="first-of-type:pt-2 [&:not(:first-of-type)]:border-t [&:not(:first-of-type)]:border-[#F0F4F7] [&:not(:first-of-type)]:pt-6"
    >
      <div className="px-5 pb-5 text-[14px] font-[500] leading-[130%] text-[#3D3D3D]">
        {title}
      </div>
      <div className="pb-5">{children}</div>
    </div>
  )
}
